import React, { lazy, Suspense, useEffect, useMemo } from 'react'
import { AuthRoute, NotFound, Route, Switch, useLocation, useParams, useQueryParams, Redirect } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { setAppLocales } from '@fs/zion-locale'
import { useUser } from '@fs/zion-user'
import { useCanAddPersonalData } from '@fs/zion-permissions'
import { NoticeLoading } from '@fs/zion-icon'
import { TreeProvider, useDesigns } from './hooks'
import sites from './sites'

const TreePage = lazy(() => import('./pages/TreePage/TreePage'))
const MediaBanners = lazy(() => import('@fs/zion-media-banners'))

export default function App() {
  const { designsByStyle } = useDesigns()
  const location = useLocation()

  // The useCanAddPersonalData hook is used in conjuction with the requirePersonalData prop due to use wanting to show the landing page initially.
  const [canAddPersonalData] = useCanAddPersonalData()
  const { signedIn, userLoading } = useUser()
  const canView = !signedIn || canAddPersonalData

  const SUPPORTED_DISPLAY_LOCALES = ['ar', 'id', 'km', 'ms', 'th', 'vi']

  useEffect(() => {
    if (!SUPPORTED_DISPLAY_LOCALES.includes('zh-hans')) {
      SUPPORTED_DISPLAY_LOCALES.push('zh-hans')
    }
    setAppLocales(SUPPORTED_DISPLAY_LOCALES)
  })

  const styleNames = Object.keys(designsByStyle).join('|')
  const siteNames = Object.keys(sites).join('|')
  const isLandingPage = useMemo(
    () => new RegExp(`^/(${siteNames})/?$`).test(location.pathname),
    [siteNames, location.pathname]
  )

  // Wait for user auth and personal data check to resolve before rendering routes.
  // useUser starts with signedIn:false/userLoading:true, so without this guard,
  // WrappedTreePage mounts once under requirePersonalData=false, then remounts
  // when signedIn flips to true (and canAddPersonalData is still null), triggering
  // duplicate hourglass API calls.
  if (userLoading || (signedIn && canAddPersonalData == null)) return <NoticeLoading />

  return (
    <ErrorBoundary>
      {isLandingPage && (
        <Suspense fallback={<NoticeLoading />}>
          <MediaBanners />
        </Suspense>
      )}
      <Suspense>
        <Switch>
          <Route exact path="/" requirePersonalData={!canView}>
            <Redirect to="/global" />
          </Route>
          {Object.keys(sites).map((path) => {
            return (
              <AuthRoute
                key={path}
                component={WrappedTreePage}
                exact
                path={`/:siteName(${path})/:style(${styleNames})/:design/:personId?`}
                requirePersonalData={!canView}
              />
            )
          })}
          {Object.entries(sites).map(([path, component]) => {
            return (
              <Route key={path} component={component} path={`/:siteName(${path})`} requirePersonalData={!canView} />
            )
          })}
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  )
}

function WrappedTreePage() {
  const { style, design } = useParams()
  const { designsByStyle } = useDesigns()
  const hasSpousePedigree = Boolean(designsByStyle[style]?.[design]?.options?.hasSpousePedigree)

  const { query } = useQueryParams()
  const forceBuildMode =
    query.buildMode !== undefined ? query.buildMode === 'true' || query.buildMode === "'true'" : undefined
  const initialBackgroundOpacity = query.backgroundOpacity ? Number.parseInt(query.backgroundOpacity, 10) : undefined
  const fromMemoryBooks = query?.from?.includes('/memory-books/')
  const pageNumber = query?.pageNumber ? Number.parseInt(query.pageNumber, 10) : undefined

  // Parse visibility options from query params
  const initialShowAvatars = query.showAvatars !== undefined ? query.showAvatars === 'true' : undefined
  const initialShowPortraits = query.showPortraits !== undefined ? query.showPortraits === 'true' : undefined
  const initialHiddenIds =
    query.hiddenIds && query.hiddenIds.length > 0 ? query.hiddenIds.split(',').filter((id) => id.length > 0) : undefined

  return (
    <TreeProvider
      hasSpousePedigree={hasSpousePedigree}
      initialBackgroundOpacity={initialBackgroundOpacity}
      initialHiddenIds={initialHiddenIds}
      initialShowAvatars={initialShowAvatars}
      initialShowPortraits={initialShowPortraits}
    >
      <TreePage
        forceBuildMode={forceBuildMode}
        fromMemoryBooks={fromMemoryBooks}
        fromUrl={query.from}
        memoryBooksPageNumber={pageNumber}
      />
    </TreeProvider>
  )
}
